/******/ (() => { // webpackBootstrap
class CartQuantity extends HTMLElement {
  static queue = Promise.resolve();
  static pending = new Map();
  static cart = null;

  static propertiesKey(value) {
    const properties = typeof value === 'string' ? JSON.parse(value || '{}') : value;
    return JSON.stringify(Object.fromEntries(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b))));
  }

  get itemIdentity() {
    return JSON.stringify([this.getAttribute('data-item-variant-id') || this.getAttribute('data-item-key'),
      CartQuantity.propertiesKey(this.getAttribute('data-item-properties')), this.getAttribute('data-item-selling-plan') || '']);
  }

  findCurrentItem(cart) {
    const key = this.getAttribute('data-item-key');
    return cart.items.find(item => item.key === key) || cart.items.find(item =>
      String(item.variant_id) === this.getAttribute('data-item-variant-id') &&
      CartQuantity.propertiesKey(item.properties) === CartQuantity.propertiesKey(this.getAttribute('data-item-properties')) &&
      String(item.selling_plan_allocation?.selling_plan?.id || '') === (this.getAttribute('data-item-selling-plan') || ''));
  }

  static syncPendingState() {
    document.querySelectorAll('cart-quantity').forEach(element => {
      if (!(element instanceof CartQuantity) || !element.getAttribute('data-item-key')) return;
      const pending = CartQuantity.pending.get(element.itemIdentity);
      if (pending) element.setLoadingState();
      else element.hideLoadingState();
      const card = element.closest('.cart-drawer__product_card');
      if (card) { card.hidden = pending?.quantity === 0; card.style.display = card.hidden ? 'none' : ''; }
    });
    document.querySelectorAll('cart-drawer [name="checkout"]').forEach(button => {
      if (CartQuantity.pending.size) {
        if (!button.hasAttribute('data-cart-pending-disabled')) button.setAttribute('data-cart-pending-disabled', String(button.disabled));
        button.disabled = true;
      } else if (button.hasAttribute('data-cart-pending-disabled')) {
        button.disabled = button.getAttribute('data-cart-pending-disabled') === 'true';
        button.removeAttribute('data-cart-pending-disabled');
      }
    });
    document.querySelectorAll('[data-cart-update-status]').forEach(status => { status.hidden = !CartQuantity.pending.size; });
  }

  constructor() {
    super();
    this.removeButton = this.querySelector('[data-cart-remove-item]');
    this.adjustButtons = this.querySelectorAll('[data-cart-quantity-button]');
    this.input = this.querySelector('[data-cart-quantity-input]');
    this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleRemoveItem = this.handleRemoveItem.bind(this);
    this.updateCart = this.updateCart.bind(this);
    this.updateLineItem = this.updateLineItem.bind(this);
    this.updateCartCountIndicator = this.updateCartCountIndicator.bind(this);
    this.handleCartErrors = this.handleCartErrors.bind(this);
    this.setLoadingState = this.setLoadingState.bind(this);
    this.hideLoadingState = this.hideLoadingState.bind(this);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-cart-quantity'});
    this.removeButton?.addEventListener('click', this.handleRemoveItem);
    this.adjustButtons.forEach(button => button.addEventListener('click', this.handleButtonClick));
    this.input?.addEventListener('change', this.handleInputChange);
    CartQuantity.syncPendingState();
  }

  handleButtonClick(e) {
    /* ===== Handle the button click event ===== */
    e.preventDefault();
    const previousValue = this.input.value;
    const adjustment = e.currentTarget.name === 'plus' ? 1 : -1;

    // Adjust the quantity by the adjustment value
    this.adjustQuantity(adjustment);

    // Process the new quantity if quantity has changed
    if (previousValue !== this.input.value) {
      this.handleQuantityChange(this.input.value);
    }
  }

  adjustQuantity(adjustment) {
    /* ===== Adjust the quantity by the adjustment value ===== */
    const newValue = parseInt(this.input.value) + adjustment;
    // If the new value is not a number, set it to 1
    this.input.value = isNaN(newValue) ? 1 : newValue;

    // If the value is smaller than the min attribute, set it to the min attribute
    if (this.input.min && parseInt(this.input.value) < parseInt(this.input.min)) {
      this.input.value = this.input.min;
    }
  }

  handleInputChange(e) {
    /* ===== Handle the change event ===== */
    e.preventDefault();

    // If the value is not a number, set it to 1
    if (isNaN(this.input.value)) {
      this.input.value = 1;
    }

    // If the value is smaller than the min attribute, set it to the min attribute
    if (this.input.min && parseInt(this.input.value) < parseInt(this.input.min)) {
      this.input.value = this.input.min;
    }

    // Process the new quantity
    this.handleQuantityChange(this.input.value);
  }

  handleQuantityChange(inputValue) {
    /* ===== Update the cart with the new quantity ===== */
    const quantity = parseInt(inputValue);
    this.updateCart(this.itemIndex, quantity);
  }

  handleRemoveItem() {
    /* ===== Set quantity of line item to zero ===== */
    this.updateCart(this.itemIndex, 0);
  }

  async updateCart(line, quantity) {
    if (!this.getAttribute('data-item-key')) return this.updateLegacyCart(line, quantity);
    const identity = this.itemIdentity;
    if (CartQuantity.pending.has(identity)) return CartQuantity.pending.get(identity).promise;
    if (!CartQuantity.pending.size) CartQuantity.cart = null;
    const pending = { quantity, focusLabel: document.activeElement?.getAttribute('aria-label') };
    CartQuantity.pending.set(identity, pending);
    this.setLoadingState();
    CartQuantity.syncPendingState();
    const run = async () => {
      try {
        const current = CartQuantity.cart ? this.findCurrentItem(CartQuantity.cart) : null;
        if (CartQuantity.cart && !current) {
          if (quantity === 0) return;
          throw new Error('This item changed. Please try again.');
        }
        const target = current?.key || this.getAttribute('data-item-key') || line;
        let response = await this.updateLineItem(target, quantity);
        if (!response) throw new Error('Unable to update your cart. Please try again.');
        if (this.hasGiftWrap) {
          const adjustment = quantity - (current?.quantity ?? this.previousQuantity);
          const giftLine = this.getGiftWrapLine(response);
          if (giftLine && this.getGiftWrapQuantity(response)) {
            response = await this.updateLineItem(response.items[giftLine - 1].key, Math.max(0, this.getGiftWrapQuantity(response) + adjustment));
            if (!response) throw new Error('Unable to update gift wrap. Please try again.');
          }
        }
        CartQuantity.cart = response;
        CartQuantity.pending.delete(identity);
        window.eventBus.emit('update:cart:drawer', response);
        this.updateCartCountIndicator(response);
        if (quantity > 0 && !CartQuantity.pending.size && pending.focusLabel) {
          const control = Array.from(document.querySelectorAll('cart-drawer [aria-label]')).find(element => element.getAttribute('aria-label') === pending.focusLabel && !element.disabled);
          control?.focus({ preventScroll: true });
        }
      } catch (error) {
        CartQuantity.cart = null;
        const liveElement = Array.from(document.querySelectorAll('cart-quantity')).find(element => element.itemIdentity === identity) || this;
        liveElement.handleCartErrors(liveElement.itemIndex, (/fetch|network|offline/i.test(error.message || '') ? "We couldn't update your cart. " : (error.message ? error.message + ' ' : '')) + 'Please try again.');
      } finally {
        CartQuantity.pending.delete(identity);
        this.hideLoadingState();
        CartQuantity.syncPendingState();
      }
    };
    pending.promise = CartQuantity.queue.then(run, run);
    CartQuantity.queue = pending.promise;
    return pending.promise;
  }

  async updateLegacyCart(line, quantity) {
    try {
      /* ===== Set the loading state ===== */
      this.setLoadingState();

      /* ===== Update the line item quantity ===== */
      let response = await this.updateLineItem(line, quantity);
      if (!response) return;

      /* ===== If successful response and has gift wrap, update the gift wrap product quantity ===== */
      if (this.hasGiftWrap) {
        const adjustment = quantity - this.previousQuantity;
        const cart = await this.getCart();
        if (this.getGiftWrapLine(cart) && this.getGiftWrapQuantity(cart)) {
          response = await this.updateLineItem(this.getGiftWrapLine(cart), this.getGiftWrapQuantity(cart) + adjustment);
        }
      }
      if (!response) return;

      /* ===== If successful response, re-render the cart ===== */
      window.eventBus.emit('update:cart:drawer', response);

      /* ===== Update cart count ===== */
      this.updateCartCountIndicator();
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  async updateLineItem(line, quantity) {
    if (!this.getAttribute('data-item-key')) return this.updateLegacyLineItem(line, quantity);
    try {
      /* ===== Update line item quantity ===== */
      const body = JSON.stringify({
        ...(typeof line === 'string' ? { id: line } : { line }),
        quantity,
        sections: 'cart-drawer'
      });

      const response = await fetch(window.routes.cart_change_url, { 
        ...this.fetchConfig(),
        body
      });

      const responseText = await response.text();

      if (!responseText) {
        this.handleCartErrors();
        return null;
      }

      const parsedResponse = JSON.parse(responseText);

      /* ===== Handle errors ===== */
      if (!response.ok || parsedResponse.errors || parsedResponse.status >= 400) {
        throw new Error(parsedResponse.description || parsedResponse.errors || 'Unable to update your cart. Please try again.');
      }

      return parsedResponse;
    } catch (error) {
      throw error;
    }
  }

  async updateLegacyLineItem(line, quantity) {
    try {
      /* ===== Update line item quantity ===== */
      const body = JSON.stringify({
        line,
        quantity,
        sections: 'cart-drawer'
      });

      const response = await fetch(window.routes.cart_change_url, { 
        ...this.fetchConfig(),
        body
      });

      const responseText = await response.text();

      if (!responseText) {
        this.handleCartErrors();
        return null;
      }

      const parsedResponse = JSON.parse(responseText);

      /* ===== Handle errors ===== */
      if (parsedResponse.errors) {
        this.handleCartErrors(line, parsedResponse.errors);
        return null;
      }

      return parsedResponse;
    } catch (error) {
      console.error('Error updating cart line item:', error);
    }
  }

  fetchConfig(type = 'json') {
    /* ===== Create the fetch configuration object ===== */
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  }

  handleCartErrors(line, errors) {
    /* ===== Reset loading state and input value ===== */
    this.hideLoadingState();
    this.input.value = this.input.getAttribute('value');

    /* ===== Display the error message ===== */
    this.renderErrorMessage(line, errors);
  }

  renderErrorMessage(line, message) {
    /* ===== Display the error message ===== */
    const errorElement = document.querySelector(`[data-cart-error-index="${line}"]`);
    if (!errorElement) return;
    errorElement.textContent = String(message);
    errorElement.setAttribute('aria-hidden', false);

    /* ===== If we're at the bottom of the cart, scroll error message into view ===== */
    const isLastItem = errorElement.getAttribute('data-cart-is-last-item') === 'true';
    if (isLastItem) {
      errorElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  async getCart() {
    /* ===== Fetch the cart information ===== */
    try {
      const response = await fetch(`${window.routes.cart_url}?view=compare`);
      return await response.json();
    } catch (error) {
      console.error(`Error fetching cart: ${error.description || error.message}`);
    }
  }

  updateCartCountIndicator(cart) {
    if (cart) window.wetheme.updateCartCount(cart);
    else if (this.cartCountIndicator) this.getCart().then(current => window.wetheme.updateCartCount(current));
  }

  getGiftWrapLine(cart) {
    return cart.items.findIndex(item => item.variant_id === this.giftWrapVariantId) + 1 || null;
  }

  getGiftWrapQuantity(cart) {
    return cart.items.find(item => item.variant_id === this.giftWrapVariantId)?.quantity || null;
  }

  setLoadingState() {
    this.classList.add('is-loading');
    if (!this.getAttribute('data-item-key')) return;
    this.setAttribute('aria-busy', 'true');
    [this.removeButton, this.input, ...this.adjustButtons].filter(Boolean).forEach(control => { control.disabled = true; });
  }

  hideLoadingState() {
    this.classList.remove('is-loading');
    if (!this.getAttribute('data-item-key')) return;
    this.removeAttribute('aria-busy');
    [this.removeButton, this.input, ...this.adjustButtons].filter(Boolean).forEach(control => { control.disabled = false; });
  }

  get itemIndex() {
    return parseInt(this.getAttribute('data-item-index'));
  }

  get previousQuantity() {
    return parseInt(this.getAttribute('data-item-quantity'));
  }

  get hasGiftWrap() {
    return this.getAttribute('data-has-gift-wrap') === "true";
  }

  get giftWrapVariantId() {
    return parseInt(this.getAttribute('data-gift-wrap-variant-id'));
  }
}

customElements.define('cart-quantity', CartQuantity);
/******/ })()
;